/**
 * PinnedAssetsList — rolls up useFavourites() into a short "Pinned"
 * section in the sidebar so starred assets and investigations are one
 * click away from any page.
 *
 * Rendered inside the Sidebar component; hides itself when no items are
 * pinned to avoid an empty section.
 */
import { Link } from 'react-router-dom'
import { Star, FileText, Box } from 'lucide-react'
import { useFavourites } from '../../hooks/useFavourites'

interface Props {
  limit?: number
  onNavigate?: () => void
}

export function PinnedAssetsList({ limit = 5, onNavigate }: Props) {
  const { items } = useFavourites()
  const visible = items.slice(0, limit)

  if (visible.length === 0) return null

  return (
    <div className="mt-2 mb-3 px-2">
      <div className="flex items-center gap-1.5 px-2 mb-1 text-[10px] uppercase tracking-wider text-surface-500">
        <Star size={10} className="text-amber-400" fill="currentColor" />
        Pinned
      </div>
      <ul className="space-y-0.5">
        {visible.map((item) => {
          const target =
            item.target ??
            (item.kind === 'asset'
              ? `/assets/${item.id}`
              : `/brain/investigation/${item.id}`)
          const Icon = item.kind === 'investigation' ? FileText : Box
          return (
            <li key={item.id}>
              <Link
                to={target}
                onClick={onNavigate}
                className="flex items-center gap-2 rounded-md px-2 py-1 text-xs text-surface-300 hover:bg-surface-800 hover:text-surface-100 transition-colors"
                title={item.label}
              >
                <Icon size={12} className="shrink-0 text-surface-500" />
                <span className="truncate">{item.label}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default PinnedAssetsList
